import React, { useEffect, useState } from "react";
import { Form, Upload, Button, Typography, Row, Col, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { submitManualKycStep, getUserDetails } from "../../redux/slices/AuthSlice";
import { getAllStates } from "../../redux/slices/ProfileSlice";
import {
  USERTYPE_ID,
  BTN_TXT,
  TITLE_TXT,
  DESC_TXT,
  ERROR_MSGS,
  MANUAL_KYC_LABELS,
  MANUAL_KYC_MSG,
  MANUAL_KYC_UPLOAD_ACCEPT,
  MANUAL_KYC_UPLOAD_HINT,
} from "../../constants/constants";
import CustomInput from "../../components/CustomInput/Input";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import CancelButton from "../../components/CustomButton/CancelButton";
import {
  panRules,
  hufPanRules,
  gstinRules,
  ifscRules,
  accNumberRules,
  nameRules,
  address1Rules,
  cityRules,
  pincodeRules,
  aadhaarNumberRules,
  bankNameRules,
  branchNameRules,
  validateManualKycUploadFile,
  capitalizeWords,
  address2LengthRules,
} from "../../helpers/helpers";
import {
  buildManualKycFormData,
  getAsyncErrorMessage,
  getNextManualStepIndex,
  getStep1Labels,
  getStep2Labels,
  isEntityOrNbfcUser,
  isNonIndividualUser,
  validateManualKycUploads,
} from "../../helpers/manualKyc";
import LiveSelfieCapture from "../../components/LiveSelfieCapture/LiveSelfieCapture";
import PrimaryButton from "../../components/CustomButton/PrimaryButton";
import CustomButton from "../../components/CustomButton/Button";

const { Title } = Typography;

const UploadField = ({ label, fieldName, fileList, onChange }) => (
  <Form.Item label={label} required>
    <Upload
      beforeUpload={(file) => {
        const error = validateManualKycUploadFile(file);
        if (error) {
          message.error(error);
          return Upload.LIST_IGNORE;
        }
        return false;
      }}
      onChange={(info) => onChange(info, fieldName)}
      fileList={fileList[fieldName] || []}
      maxCount={1}
      accept={MANUAL_KYC_UPLOAD_ACCEPT}
    >
      <Button icon={<UploadOutlined />}>{BTN_TXT.CLICK_TO_UPLOAD}</Button>
    </Upload>
    <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
      {MANUAL_KYC_UPLOAD_HINT}
    </div>
  </Form.Item>
);

const ManualKycForm = ({
  currentStep,
  userTypeId,
  onSubmitSuccess,
  onNextStep,
  onCancel,
  onMaybeLater,
  hideCancel,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { loading, user } = useSelector((state) => state.auth);
  const { statesData } = useSelector((state) => state.profile);

  const [fileList, setFileList] = useState({});
  const [selfieFile, setSelfieFile] = useState(null);

  const stateOptions = (statesData || []).map((s) => ({
    value: s.stateId,
    label: capitalizeWords(s.stateName),
  }));

  useEffect(() => {
    dispatch(getAllStates());
  }, [dispatch]);

  React.useEffect(() => {
    form.resetFields();
    setFileList({});
    setSelfieFile(null);
  }, [currentStep, form]);

  const handleFileChange = (info, fieldName) => {
    const latest = info.fileList.slice(-1);
    if (latest[0]) {
      const fileObj = latest[0].originFileObj || latest[0];
      const error = validateManualKycUploadFile(fileObj);
      if (error) {
        message.error(error);
        setFileList({ ...fileList, [fieldName]: [] });
        return;
      }
    }
    setFileList({ ...fileList, [fieldName]: latest });
  };

  const isHuf = userTypeId === USERTYPE_ID.HUF;
  const isEntityOrNbfc = isEntityOrNbfcUser(userTypeId);
  const isNonIndividual = isNonIndividualUser(userTypeId);

  const onFinish = async (values) => {
    const backendStep = currentStep + 1;
    const uploadError = validateManualKycUploads({
      backendStep,
      isNonIndividual,
      isHuf,
      isEntityOrNbfc,
      fileList,
      selfieFile,
    });
    if (uploadError) {
      message.error(uploadError);
      return;
    }

    const formData = buildManualKycFormData({
      backendStep,
      userTypeId,
      values,
      fileList,
      selfieFile,
    });

    try {
      const actionResult = await dispatch(submitManualKycStep(formData));
      if (submitManualKycStep.fulfilled.match(actionResult)) {
        const status =
          actionResult.payload?.statusCode || actionResult.payload?.status;
        if (status === 200 || status === 201) {
          message.success(MANUAL_KYC_MSG.STEP_SAVED(backendStep));
          await dispatch(getUserDetails());
          const nextStepIdx = getNextManualStepIndex({
            backendStep,
            isNonIndividual,
            s2Status:
              user?.step2Status || user?.businessProfile?.step2Status,
          });
          if (nextStepIdx !== null) {
            onNextStep?.(nextStepIdx);
          } else {
            onSubmitSuccess();
          }
        } else {
          message.error(
            getAsyncErrorMessage(
              actionResult.payload,
              MANUAL_KYC_MSG.SUBMIT_FAILED,
            ),
          );
        }
      } else {
        message.error(
          getAsyncErrorMessage(
            actionResult.payload,
            MANUAL_KYC_MSG.SUBMIT_ERROR,
          ),
        );
      }
    } catch (error) {
      message.error(
        getAsyncErrorMessage(error, MANUAL_KYC_MSG.SUBMISSION_FAILED),
      );
    }
  };

  const renderStepFields = () => {
    if (currentStep === 0) {
      const { panLabel, nameLabel, selfieLabel } = getStep1Labels({
        isHuf,
        isEntityOrNbfc,
      });

      return (
        <>
          <Title level={5}>{TITLE_TXT.MANUAL_STEP1_TITLE}</Title>
          <p className="upload-hint">{DESC_TXT.MANUAL_STEP1_DESC}</p>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <CustomInput
                label={panLabel}
                name="pan"
                placeholder={`Enter ${panLabel}`}
                rules={panRules}
              />
            </Col>
            <Col xs={24} sm={12}>
              <CustomInput
                label={nameLabel}
                name="fullName"
                placeholder={`Enter ${nameLabel}`}
                rules={nameRules("Full")}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <CustomInput
                label={MANUAL_KYC_LABELS.AADHAAR_NUMBER}
                name="idNumber"
                placeholder={MANUAL_KYC_LABELS.AADHAAR_PLACEHOLDER}
                rules={aadhaarNumberRules}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <CustomInput
                label={MANUAL_KYC_LABELS.ADDRESS_LINE_1}
                name="addressLine1"
                placeholder={`Enter ${MANUAL_KYC_LABELS.ADDRESS_LINE_1}`}
                rules={address1Rules}
              />
            </Col>
            <Col xs={24} sm={12}>
              <CustomInput
                label={MANUAL_KYC_LABELS.ADDRESS_LINE_2}
                name="addressLine2"
                placeholder={`Enter ${MANUAL_KYC_LABELS.ADDRESS_LINE_2}`}
                rules={address2LengthRules}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <CustomInput
                label={MANUAL_KYC_LABELS.CITY}
                name="city"
                placeholder={`Enter ${MANUAL_KYC_LABELS.CITY}`}
                rules={cityRules}
              />
            </Col>
            <Col xs={24} sm={8}>
              <CustomSelect
                label={MANUAL_KYC_LABELS.STATE}
                name="stateId"
                showSearch
                placeholder={`Select ${MANUAL_KYC_LABELS.STATE}`}
                options={stateOptions}
                className="custom-kyc-form-select"
                rules={[
                  { required: true, message: ERROR_MSGS.SEL_STATE },
                ]}
              />
            </Col>
            <Col xs={24} sm={8}>
              <CustomInput
                label={MANUAL_KYC_LABELS.PINCODE}
                name="pincode"
                placeholder={`Enter ${MANUAL_KYC_LABELS.PINCODE}`}
                rules={pincodeRules}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <UploadField
                label={`${panLabel} Image`}
                fieldName="panDocument"
                fileList={fileList}
                onChange={handleFileChange}
              />
            </Col>
            <Col xs={24} sm={12}>
              <UploadField
                label={MANUAL_KYC_LABELS.AADHAAR_DOCUMENT}
                fieldName="idDocumentFront"
                fileList={fileList}
                onChange={handleFileChange}
              />
            </Col>
          </Row>

          <LiveSelfieCapture
            value={selfieFile}
            onChange={setSelfieFile}
            label={selfieLabel}
            required
          />
        </>
      );
    }

    if (isNonIndividual && currentStep === 1) {
      const { entityPanLabel, entityDocLabel, title } = getStep2Labels({
        isHuf,
      });

      return (
        <>
          <Title level={5}>{title}</Title>
          <p className="upload-hint">
            {isHuf ? DESC_TXT.MANUAL_STEP2_HUF_DESC : DESC_TXT.MANUAL_STEP2_DESC}
          </p>

          <Row gutter={16}>
            {isEntityOrNbfc && (
              <Col xs={24} sm={12}>
                <CustomInput
                  label={MANUAL_KYC_LABELS.GSTIN}
                  name="gstin"
                  placeholder={`Enter ${MANUAL_KYC_LABELS.GSTIN}`}
                  rules={gstinRules}
                />
              </Col>
            )}
            <Col xs={24} sm={isEntityOrNbfc ? 12 : 24}>
              <CustomInput
                label={entityPanLabel}
                name="entityPan"
                placeholder={`Enter ${entityPanLabel}`}
                rules={isHuf ? hufPanRules : panRules}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            {isEntityOrNbfc && (
              <Col xs={24} sm={12}>
                <UploadField
                  label={MANUAL_KYC_LABELS.GST_CERTIFICATE}
                  fieldName="gstDocument"
                  fileList={fileList}
                  onChange={handleFileChange}
                />
              </Col>
            )}
            <Col xs={24} sm={isEntityOrNbfc ? 12 : 24}>
              <UploadField
                label={entityDocLabel}
                fieldName="companyPanDocument"
                fileList={fileList}
                onChange={handleFileChange}
              />
            </Col>
          </Row>

          {isEntityOrNbfc && (
            <>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <CustomInput
                    label={MANUAL_KYC_LABELS.REGISTERED_ADDRESS_1}
                    name="addressLine1"
                    placeholder={`Enter ${MANUAL_KYC_LABELS.REGISTERED_ADDRESS_1}`}
                    rules={address1Rules}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <CustomInput
                    label={MANUAL_KYC_LABELS.ADDRESS_LINE_2}
                    name="addressLine2"
                    placeholder={`Enter ${MANUAL_KYC_LABELS.ADDRESS_LINE_2}`}
                    rules={address2LengthRules}
                  />
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <CustomInput
                    label={MANUAL_KYC_LABELS.CITY}
                    name="city"
                    placeholder={`Enter ${MANUAL_KYC_LABELS.CITY}`}
                    rules={cityRules}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <CustomSelect
                    label={MANUAL_KYC_LABELS.STATE}
                    name="stateId"
                    showSearch
                    placeholder={`Select ${MANUAL_KYC_LABELS.STATE}`}
                    options={stateOptions}
                    className="custom-kyc-form-select"
                    rules={[
                      {
                        required: true,
                        message: ERROR_MSGS.SEL_STATE,
                      },
                    ]}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <CustomInput
                    label={MANUAL_KYC_LABELS.PINCODE}
                    name="pincode"
                    placeholder={`Enter ${MANUAL_KYC_LABELS.PINCODE}`}
                    rules={pincodeRules}
                  />
                </Col>
              </Row>
            </>
          )}
        </>
      );
    }

    return (
      <>
        <Title level={5}>{TITLE_TXT.MANUAL_STEP3_TITLE}</Title>
        <p className="upload-hint">{DESC_TXT.MANUAL_STEP3_DESC}</p>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <CustomInput
              label={MANUAL_KYC_LABELS.ACCOUNT_NUMBER}
              name="accountNumber"
              placeholder={`Enter ${MANUAL_KYC_LABELS.ACCOUNT_NUMBER}`}
              rules={accNumberRules}
            />
          </Col>
          <Col xs={24} sm={12}>
            <CustomInput
              label={MANUAL_KYC_LABELS.CONFIRM_ACCOUNT_NUMBER}
              name="confirmAccountNumber"
              placeholder={MANUAL_KYC_LABELS.CONFIRM_ACCOUNT_NUMBER}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: MANUAL_KYC_MSG.CONFIRM_ACC_REQUIRED,
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || !String(value).trim()) {
                      return Promise.resolve();
                    }
                    if (!/^\d+$/.test(String(value).trim())) {
                      return Promise.reject(
                        new Error("Account number should contain only numbers."),
                      );
                    }
                    if (getFieldValue("accountNumber") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(MANUAL_KYC_MSG.ACC_MISMATCH),
                    );
                  },
                }),
              ]}
            />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <CustomInput
              label={MANUAL_KYC_LABELS.IFSC}
              name="ifscCode"
              placeholder={`Enter ${MANUAL_KYC_LABELS.IFSC}`}
              rules={ifscRules}
            />
          </Col>
          <Col xs={24} sm={12}>
            <CustomInput
              label={MANUAL_KYC_LABELS.ACCOUNT_HOLDER}
              name="accountHolderName"
              placeholder={`Enter ${MANUAL_KYC_LABELS.ACCOUNT_HOLDER}`}
              rules={nameRules("Account Holder")}
            />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <CustomInput
              label={MANUAL_KYC_LABELS.BANK_NAME}
              name="bankName"
              placeholder={`Enter ${MANUAL_KYC_LABELS.BANK_NAME}`}
              rules={bankNameRules}
            />
          </Col>
          <Col xs={24} sm={8}>
            <CustomInput
              label={MANUAL_KYC_LABELS.BRANCH_NAME}
              name="branchName"
              placeholder={`Enter ${MANUAL_KYC_LABELS.BRANCH_NAME}`}
              rules={branchNameRules}
            />
          </Col>
          <Col xs={24} sm={8}>
            <CustomInput
              label={MANUAL_KYC_LABELS.BRANCH_PINCODE}
              name="pincode"
              placeholder={`Enter ${MANUAL_KYC_LABELS.BRANCH_PINCODE}`}
              rules={pincodeRules}
            />
          </Col>
        </Row>

        <UploadField
          label={MANUAL_KYC_LABELS.BANK_PROOF}
          fieldName="cancelledCheque"
          fileList={fileList}
          onChange={handleFileChange}
        />
      </>
    );
  };

  return (
    <div className="manual-kyc-form">
      <Form form={form} layout="vertical" colon={false} onFinish={onFinish}>
        <div className="form-body">{renderStepFields()}</div>
        <div className="form-actions">
          <div className="left-actions">
            {onMaybeLater && (
              <CustomButton
                color="purple"
                variant="outlined"
                onClick={onMaybeLater}
                style={{ padding: "24px 32px" }}
              >
                {BTN_TXT.MAYBE_LATER}
              </CustomButton>
            )}
          </div>
          <div className="right-actions">
            {!hideCancel && <CancelButton onClick={onCancel} />}
            <PrimaryButton type="primary" htmlType="submit" loading={loading}>
              {loading ? BTN_TXT.UPLOADING : BTN_TXT.SUBMIT}
            </PrimaryButton>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default ManualKycForm;
